import ContentIllustration from '../../components/ui/ContentIllustration';
import SiteFooter from '../../components/layout/SiteFooter';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Mail, Loader2, Lock, Printer, X as XIcon } from 'lucide-react';
import api from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';

/**
 * /reussir-ton-concours-qcm
 *
 * Page éditoriale pour les lecteurs du livre KDP "Réussir ton concours
 * à QCM sans tout oublier". 4 ressources à imprimer sans inscription
 * + un encart de transparence sur MonQuiz.
 *
 * Gate : tant que `book_landing_public` est false, seuls les admins voient
 * la page (le backend renvoie allowed=false sinon). Le bouton de bascule
 * est sur /gestion (carte "Landing livre").
 */

const RESOURCES = [
    {
        slug: 'planning-12-semaines',
        title: 'Planning 12 semaines',
        desc: 'Le cadre J-90 → J-1 en 6 tableaux : phases, semaines types, ratios encodage/récupération, versions courtes.',
        source: 'Annexe C du livre',
        icon: 'calendar',
    },
    {
        slug: 'templates-fiches',
        title: 'Templates de fiches',
        desc: 'Trois modèles à reproduire ou imprimer : Cornell pour les chapitres, flashcard pour les notions atomiques, journal de calibration pour la métacognition.',
        source: 'Annexe D du livre',
        icon: 'layers',
    },
    {
        slug: 'grille-auto-diagnostic',
        title: 'Grille d\'auto-diagnostic',
        desc: '10 questions pour te situer en 5 minutes. Refais-la dans 2 mois et compare ton score : c\'est lui qui mesure ton progrès méthodologique.',
        source: 'Chapitre 1 du livre',
        icon: 'clipboard-list',
    },
    {
        slug: 'checklist-jour-j',
        title: 'Checklist jour J',
        desc: 'Tout ce qui doit être prêt et fait — la veille, le matin, pendant l\'épreuve, et après. Imprime, plie en deux, glisse dans ta poche.',
        source: 'Chapitre 11 du livre',
        icon: 'list-checks',
    },
];

const PRINCIPES = [
    { title: 'Produire avant de vérifier', desc: 'Tu réponds avant de regarder la correction. Pas l\'inverse.' },
    { title: 'Espacer', desc: 'Tu revois au moment où ça commence à s\'effacer, pas avant.' },
    { title: 'Inconfort utile', desc: 'Si la révision est facile, elle ne fait pas mémoriser.' },
    { title: 'Alterner', desc: 'Tu mélanges les notions plutôt que tout faire en bloc.' },
    { title: 'Mesurer', desc: 'Tu suis ton écart estimé / réel, pas seulement tes scores bruts.' },
];

const OUTILS = [
    { title: 'Papier / Leitner', meilleur: 'Démarrer simplement, comprendre l\'espacement par le geste.', limite: 'Peu pratique au-delà d\'un certain volume.' },
    { title: 'Anki', meilleur: 'Flashcards personnalisées + répétition espacée robuste.', limite: 'Tu construis tout, courbe d\'entrée rude.' },
    { title: 'Annales officielles', meilleur: 'Format réel, conditions d\'épreuve.', limite: 'Volume limité, pas de tracking automatique.' },
    { title: 'Plateforme spécialisée', meilleur: 'Banc QCM construit + suivi + révisions.', limite: 'Dépend de la couverture du concours.' },
    { title: 'IA généraliste', meilleur: 'Reformuler, générer des variantes, simuler un oral.', limite: 'Hallucinations possibles, vérification obligatoire.' },
];

function AccessDeniedView() {
    return (
        <div className="livre-page">
            <div className="livre-inner">
                <div className="livre-locked">
                    <Lock size={36} />
                    <h2>Page bientôt disponible</h2>
                    <p>
                        Cette page accompagne l'ouvrage <em>« Réussir ton concours à QCM sans tout oublier »</em>. Elle s'ouvrira au public le jour de la publication du livre.
                    </p>
                    <Link to="/" className="btn btn-secondary">Retour à l'accueil</Link>
                </div>
            </div>
        </div>
    );
}

export default function ReussirTonConcoursQcm() {
    usePageTitle('Réussir ton concours à QCM — Ressources');
    const [accessState, setAccessState] = useState({ loading: true, allowed: false });
    const [openError, setOpenError] = useState(null);
    const [preview, setPreview] = useState(null); // { url, title, slug, blob } | null
    const [previewLoading, setPreviewLoading] = useState(false);
    const iframeRef = useRef(null);

    useEffect(() => {
        api.get('/livre/access.php')
            .then(({ data }) => setAccessState({ loading: false, allowed: !!data.data?.allowed, isAdmin: !!data.data?.is_admin, public: !!data.data?.public }))
            .catch(() => setAccessState({ loading: false, allowed: false }));
    }, []);

    // Cleanup blob URL au démontage
    useEffect(() => {
        return () => {
            if (preview?.url) window.URL.revokeObjectURL(preview.url);
        };
    }, [preview?.url]);

    // ESC ferme le modal
    useEffect(() => {
        if (!preview) return;
        const onKey = (e) => { if (e.key === 'Escape') closePreview(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [preview]);

    const openResource = async (slug, title) => {
        setPreviewLoading(true);
        setOpenError(null);
        try {
            const response = await api.get(`/livre/download.php?slug=${slug}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            setPreview({ url, title, slug, blob: response.data });
        } catch (err) {
            setOpenError(err.response?.status === 403
                ? 'Cette ressource n\'est pas encore accessible publiquement.'
                : 'Erreur lors de l\'ouverture de la fiche.');
            setTimeout(() => setOpenError(null), 4000);
        }
        setPreviewLoading(false);
    };

    const closePreview = () => {
        if (preview?.url) window.URL.revokeObjectURL(preview.url);
        setPreview(null);
    };

    const printPreview = () => {
        try {
            iframeRef.current?.contentWindow?.focus();
            iframeRef.current?.contentWindow?.print();
        } catch (e) { /* ignore */ }
    };

    if (accessState.loading) {
        return (
            <div className="livre-page">
                <div className="livre-inner" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                    <Loader2 size={28} className="spin" />
                </div>
            </div>
        );
    }

    if (!accessState.allowed) return <AccessDeniedView />;

    return (
        <div className="livre-page">
            <div className="livre-inner">

                {/* Bandeau admin si pré-publication */}
                {accessState.isAdmin && !accessState.public && (
                    <div className="livre-admin-banner">
                        <Lock size={14} /> Page en pré-publication — visible uniquement par les admins. Bascule en public depuis <Link to="/gestion">/gestion</Link>.
                    </div>
                )}

                {/* === 1. Accueil === */}
                <section className="livre-section livre-section--hero">
                    <h1 className="livre-title">
                        Tu as fini le livre.<br />
                        <span className="livre-title-accent">Et maintenant ?</span>
                    </h1>
                    <div className="livre-hero-text">
                        <p>
                            Cette page existe pour te donner les outils pratiques évoqués dans les annexes, sortis du livre et mis en page pour l'A4 — prêts à être complétés à la main et collés au mur. Tu les trouves ci-dessous, <strong>sans inscription, sans formulaire, sans email à laisser</strong>. Tu ouvres la page, tu imprimes ce dont tu as besoin, tu fais ce que tu veux avec.
                        </p>
                        <p>
                            Plus bas, tu trouveras aussi quelques pistes d'outils numériques pour appliquer la méthode au quotidien — y compris <strong>MonQuiz</strong>, la plateforme que je développe. Ce n'est pas un passage obligé : la méthode tient sans aucune plateforme, et le chapitre 9 te l'explique déjà.
                        </p>
                    </div>
                </section>

                {/* === 2. Méthode en une page === */}
                <section className="livre-section">
                    <h2 className="livre-section-title">La méthode en une page</h2>
                    <p className="livre-section-sub">
                        5 principes que tu vas pouvoir matérialiser avec les fiches qui suivent.
                    </p>
                    <div className="livre-principes">
                        {PRINCIPES.map((p, i) => (
                            <div key={i} className="livre-principe">
                                <span className="livre-principe-num">{String(i + 1).padStart(2, '0')}</span>
                                <strong>{p.title}</strong>
                                <span>{p.desc}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* === 3. Les 4 ressources === */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Les 4 fiches à imprimer</h2>
                    <p className="livre-section-sub">
                        Mise en page A4, prête pour l'impression. Cliquer ouvre la fiche : le bouton <strong>Imprimer cette fiche</strong> est en haut, et <kbd>Ctrl</kbd>+<kbd>P</kbd> fait la même chose.
                    </p>
                    <div className="livre-resources-grid">
                        {RESOURCES.map(r => {

                            return (
                                <div key={r.slug} className="livre-resource-card">
                                    <div className="livre-resource-icon"><ContentIllustration name={r.icon} size={56} /></div>
                                    <h3>{r.title}</h3>
                                    <p>{r.desc}</p>
                                    <span className="livre-resource-source">{r.source}</span>
                                    <button onClick={() => openResource(r.slug, r.title)} className="livre-resource-btn" disabled={previewLoading}>
                                        {previewLoading ? <Loader2 size={15} className="spin" /> : <Printer size={15} />} Voir la fiche
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Combiné */}
                    <div className="livre-combined-block">
                        <div>
                            <h4>Toutes les fiches en une seule</h4>
                            <p>
                                Préfère un document unique avec page de garde, sommaire et les 4 fiches enchaînées ? Ouvre la version combinée et imprime tout d'un coup.
                            </p>
                        </div>
                        <button onClick={() => openResource('toutes-les-fiches', 'Toutes les fiches')} className="btn btn-primary" disabled={previewLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {previewLoading ? <Loader2 size={15} className="spin" /> : <Printer size={15} />} Toutes les fiches
                        </button>
                    </div>

                    {openError && (
                        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: '0.88em' }}>
                            {openError}
                        </div>
                    )}
                </section>

                {/* === 4. Choisir un outil principal === */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Choisir ton outil principal</h2>
                    <p className="livre-section-sub">
                        Aucun outil ne sauve une mauvaise méthode. Le meilleur outil est celui que tu utilises encore trois semaines après l'avoir installé. Lis ce tableau comme une carte, pas comme un podium.
                    </p>
                    <div className="livre-outils-grid">
                        {OUTILS.map((o, i) => (
                            <div key={i} className="livre-outil-card">
                                <h4>{o.title}</h4>
                                <div className="livre-outil-row">
                                    <span className="livre-outil-label livre-outil-label--good">+</span>
                                    <span>{o.meilleur}</span>
                                </div>
                                <div className="livre-outil-row">
                                    <span className="livre-outil-label livre-outil-label--bad">−</span>
                                    <span>{o.limite}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* === 5. MonQuiz — encart visuellement isolé === */}
                <section className="livre-monquiz-block">
                    <div className="livre-monquiz-header">
                        <Sparkles size={20} />
                        <h2>À te dire avant de continuer</h2>
                    </div>
                    <div className="livre-monquiz-transparency">
                        <p>
                            <strong>MonQuiz est mon outil.</strong> Cette page existe parce que j'ai construit la plateforme pour appliquer la méthode du livre, et je trouverais malhonnête de te recommander une solution sans te dire que c'est moi qui la développe. Je n'ai aucun intérêt à te la vendre si elle ne te correspond pas — un utilisateur qui n'utilise pas l'outil n'aide ni toi ni moi.
                        </p>
                        <p>
                            Si tu travailles déjà avec Anki et que tu t'y tiens, ne change pas. Si tu démarres avec moins de cinquante cartes, le papier suffit. Si ton concours est dans moins de huit semaines, les annales officielles passent avant toute plateforme. Les paragraphes qui suivent décrivent ce que MonQuiz fait, et ce qu'il ne fait pas, pour que tu puisses décider en connaissance de cause.
                        </p>
                    </div>

                    <div className="livre-monquiz-grid">
                        <div className="livre-monquiz-col">
                            <h3>Ce que MonQuiz fait</h3>
                            <ul>
                                <li>Banc de QCM construit par concours, avec une explication rédigée pour chaque réponse.</li>
                                <li>Quiz intelligent qui re-pioche tes faiblesses et notions oubliées.</li>
                                <li>Quiz sur erreurs ciblé (jamais réussies, instables, récemment ratées).</li>
                                <li>Suivi par tag : top 5 faiblesses + notions à revoir, sans tenir un journal manuel.</li>
                                <li>Mode <em>Révisions du jour</em> qui te liste chaque jour les questions dues selon ta courbe d'oubli personnelle.</li>
                            </ul>
                        </div>
                        <div className="livre-monquiz-col">
                            <h3>Ce qu'il ne fait pas</h3>
                            <ul>
                                <li>Pas de simulation de concours blanc en conditions chronométrées rigoureuses.</li>
                                <li>Pas un Anki personnalisable — le contenu est pré-construit.</li>
                                <li>Pas un substitut aux annales officielles pour la préparation finale.</li>
                                <li>Limité aux concours français à QCM couverts par la plateforme.</li>
                            </ul>
                        </div>
                    </div>

                    <div className="livre-monquiz-cta">
                        <Link to="/decouvrir?from=livre-qcm" className="btn btn-primary livre-monquiz-btn">
                            Voir MonQuiz <ArrowRight size={16} />
                        </Link>
                        <span className="livre-monquiz-note">Présentation détaillée + tarif transparent. Pas d'inscription requise pour découvrir.</span>
                    </div>
                </section>

                {/* === 6. Feedback === */}
                <section className="livre-section livre-section--narrow">
                    <h2 className="livre-section-title">Tu as une remarque sur le livre ?</h2>
                    <p>
                        Si une partie du livre t'a paru fausse, imprécise ou améliorable, écris-moi. C'est cette habitude du test logiciel qui m'a amené à écrire ce livre, et elle vaut aussi pour le livre lui-même. Je préfère le savoir et le corriger plutôt que défendre une formulation qui n'aide personne.
                    </p>
                    <a href="mailto:contact@monquiz.fr" className="livre-mail-link">
                        <Mail size={16} /> contact@monquiz.fr
                    </a>
                </section>

                {/* === 7. Pour finir === */}
                <section className="livre-section livre-section--coda">
                    <p>
                        <em>Bonne préparation. Et le jour J, rappelle-toi que tu n'auras pas besoin de monter au niveau de tes ambitions. Tu descendras au niveau de tes automatismes. Construis-les maintenant.</em>
                    </p>
                </section>

            </div>

            <SiteFooter />

            {/* Modal de prévisualisation des fiches */}
            {preview && (
                <div className="livre-preview-overlay" onClick={closePreview}>
                    <div className="livre-preview-modal" onClick={e => e.stopPropagation()}>
                        <div className="livre-preview-header">
                            <span className="livre-preview-title">{preview.title}</span>
                            <div className="livre-preview-actions">
                                <button onClick={printPreview} className="livre-preview-btn livre-preview-btn--primary" title="Imprimer cette fiche (Ctrl+P)">
                                    <Printer size={15} /> Imprimer cette fiche
                                </button>
                                <button onClick={closePreview} className="livre-preview-btn livre-preview-btn--close" title="Fermer (Échap)">
                                    <XIcon size={18} />
                                </button>
                            </div>
                        </div>
                        <iframe
                            ref={iframeRef}
                            src={preview.url}
                            className="livre-preview-iframe"
                            title={preview.title}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
